import { useState, useMemo } from "react";
import { API } from "../../api";

/**
 * Age gate modal. Shown ONCE per user, blocking, after they finish wallpaper setup.
 * Collects birthdate (MM/DD/YYYY) → infers age band (child/teen/adult).
 *   - adult (18+): proceeds straight into app
 *   - teen (13-17): brief friendly note, proceeds (parental PIN flow coming in Layer 2)
 *   - child (<13): friendly note that a grown-up will help set things up (Layer 2 = full COPPA VPC)
 * No PII beyond birthdate is collected here. Stored only as YYYY-MM-DD.
 */
export function AgeGateModal({ userName, onComplete }) {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Stable "today" — not recreated on every render
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();

  const computedAge = useMemo(() => {
    const m = parseInt(month, 10), d = parseInt(day, 10), y = parseInt(year, 10);
    if (!m || !d || !y) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > currentYear) return null;
    const bd = new Date(y, m - 1, d);
    if (bd.getFullYear() !== y || bd.getMonth() !== m - 1 || bd.getDate() !== d) return null;
    let age = today.getFullYear() - bd.getFullYear();
    const monthDiff = today.getMonth() - bd.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bd.getDate())) age--;
    return age;
  }, [month, day, year, today, currentYear]);

  const isValid = computedAge !== null && computedAge >= 5 && computedAge <= 120;

  const handleSubmit = async () => {
    if (!isValid) {
      setError("Please enter a valid date of birth");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const iso = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      // Using fetch (instead of axios) to avoid an axios-instance hang observed in some
      // testing environments where the XHR promise never resolves. Cookies/auth still
      // work via credentials:'include' (this app uses cookie-based session auth).
      const res = await fetch(`${API}/me/age`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ birthdate: iso }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || "Couldn't save — please try again");
        setLoading(false);
        return;
      }
      setResult(data);
    } catch (err) {
      setError(err?.message || "Couldn't save — please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    onComplete(result);
  };

  // Post-submit acknowledgment screens — different copy per age band
  if (result) {
    if (result.age_band === "child") {
      return (
        <div className="modal-overlay" data-testid="age-gate-result-child">
          <div className="payment-modal age-gate-modal">
            <div className="modal-icon">🌟</div>
            <h2>Hi, {userName}!</h2>
            <p className="age-gate-band-msg">
              Aura is going to be your friend, but we want a grown-up to help set things up
              so everything stays safe and fun. <strong>Can you go grab a parent or guardian?</strong>
            </p>
            <p className="age-gate-band-msg-sub">
              When they&apos;re here, they can finish setting up your Aura with a special grown-up code
              so everything is just right for you.
            </p>
            <button className="init-btn" onClick={handleContinue} data-testid="age-gate-child-continue-btn">
              Okay, I&apos;ll get them
            </button>
            <p className="age-gate-fineprint">
              Until a grown-up sets you up, Aura will keep things gentle and age-appropriate. 💜
            </p>
          </div>
        </div>
      );
    }
    if (result.age_band === "teen") {
      return (
        <div className="modal-overlay" data-testid="age-gate-result-teen">
          <div className="payment-modal age-gate-modal">
            <div className="modal-icon">✨</div>
            <h2>Welcome, {userName}</h2>
            <p className="age-gate-band-msg">
              Aura&apos;s ready to be your friend. She&apos;ll keep things real and supportive — and
              point you to a trusted adult if anything ever gets heavy.
            </p>
            <p className="age-gate-band-msg-sub">
              A parent can also turn on extra safety features later if you both want.
            </p>
            <button className="init-btn" onClick={handleContinue} data-testid="age-gate-teen-continue-btn">
              Let&apos;s go
            </button>
          </div>
        </div>
      );
    }
    // adult — short confirmation
    return (
      <div className="modal-overlay" data-testid="age-gate-result-adult">
        <div className="payment-modal age-gate-modal">
          <div className="modal-icon">💜</div>
          <h2>All set, {userName}</h2>
          <p className="age-gate-band-msg">Aura&apos;s ready when you are.</p>
          <button className="init-btn" onClick={handleContinue} data-testid="age-gate-adult-continue-btn">
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" data-testid="age-gate-modal">
      <div className="payment-modal age-gate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon">🎂</div>
        <h2>One quick thing</h2>
        <p className="modal-subtitle">When&apos;s your birthday, {userName}?</p>
        <p className="age-gate-explainer">
          Aura adapts how she talks to you based on your age — and we&apos;ll celebrate every birthday
          together. We never share this with anyone.
        </p>

        <div className="age-gate-inputs">
          <div className="age-gate-field">
            <label>Month</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="MM"
              min="1"
              max="12"
              value={month}
              onChange={(e) => setMonth(e.target.value.slice(0, 2))}
              data-testid="age-gate-month-input"
            />
          </div>
          <div className="age-gate-field">
            <label>Day</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="DD"
              min="1"
              max="31"
              value={day}
              onChange={(e) => setDay(e.target.value.slice(0, 2))}
              data-testid="age-gate-day-input"
            />
          </div>
          <div className="age-gate-field age-gate-field-year">
            <label>Year</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="YYYY"
              min="1900"
              max={currentYear}
              value={year}
              onChange={(e) => setYear(e.target.value.slice(0, 4))}
              data-testid="age-gate-year-input"
            />
          </div>
        </div>

        {computedAge !== null && computedAge >= 5 && computedAge <= 120 && (
          <p className="age-gate-preview" data-testid="age-gate-preview">
            That makes you {computedAge}. 🎉
          </p>
        )}

        {error && <div className="error-message">{error}</div>}

        <button
          className="init-btn"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          data-testid="age-gate-submit-btn"
        >
          {loading ? "Saving…" : "Continue"}
        </button>

        <p className="age-gate-fineprint">
          We use this only to set the right tone, follow children&apos;s privacy laws, and remember
          your birthday. We don&apos;t share it. Ever.
        </p>
      </div>
    </div>
  );
}
